import type {
  Aircraft,
  Airport,
  AirportCapability,
  CapabilityException,
  CountryCapability,
} from '@/lib/reference'
import {
  exceptionIndex,
  fleetColumnKey,
  resolveAirport,
  resolveCountry,
  tailBlockedAtAirport,
  tailBlockedByBlockTime,
  tailBlockedByCycles,
  tailBlockedInCountry,
} from '@/lib/reference/fleet'
import type { FlightLeg } from './types'

/**
 * What the reference tables say about a leg that is already flying.
 *
 * The Gantt is where reference data stops being a spreadsheet and starts
 * having consequences: a tail barred from Russia sitting on a Moscow rotation
 * is a table row nobody would have read, and a red edge on a bar nobody can
 * miss. Both tiers of fleet.ts are applied, so the verdict is the same one the
 * engine will reach later (05-DU-LIEU-THAM-CHIEU section 6).
 *
 * Deliberately NOT a rule engine. R1 (minimum ground time), R3 (slots), R5
 * (seats against booked passengers) and R7 (NOTAM) need tables the app has not
 * been given yet, and inventing a partial answer for them would be worse than
 * saying nothing. What is checked here is exactly what the loaded tables can
 * decide: the four tail limits and the two capability matrices that address a
 * place.
 */

export const ALERT_KINDS = [
  'tailCountry',
  'tailAirport',
  'tailBlock',
  'tailCycles',
  'aircraftCountry',
  'aircraftAirport',
  'unknownTail',
  'unknownStation',
] as const

export type AlertKind = (typeof ALERT_KINDS)[number]

/**
 * How hard the finding is.
 *
 * `blocking` is a standing rule the operator wrote down: the aircraft is not
 * permitted here, full stop. `warning` is a limit the shift can work around
 * and regularly does — a country limit is read against the payload on the day,
 * so a tail listed against Japan still flies it when the load allows
 * (confirmed with OMC, 15/09/2026). Drawing both the same colour would train
 * people to ignore the colour.
 */
export type AlertSeverity = 'blocking' | 'warning'

export const ALERT_SEVERITY: Record<AlertKind, AlertSeverity> = {
  tailCountry: 'warning',
  tailAirport: 'blocking',
  tailBlock: 'blocking',
  tailCycles: 'blocking',
  aircraftCountry: 'blocking',
  aircraftAirport: 'blocking',
  unknownTail: 'warning',
  unknownStation: 'warning',
}

export interface LegAlert {
  kind: AlertKind
  severity: AlertSeverity
  /** Station or country the alert is about. */
  subject: string
  /** The limit or rule that refused, in its own words. */
  detail: string
}

/** The worst severity in a list, or null when the list is empty. */
export function worstSeverity(alerts: LegAlert[]): AlertSeverity | null {
  if (alerts.some((alert) => alert.severity === 'blocking')) return 'blocking'
  return alerts.length > 0 ? 'warning' : null
}

export interface AlertContext {
  fleetByRegn: Map<string, Aircraft>
  airportsByIata: Map<string, Airport>
  countryCapability: CountryCapability[]
  airportCapability: AirportCapability[]
  /** Indexed once per board rather than scanned once per leg. */
  countryExceptions: Map<string, CapabilityException>
  airportExceptions: Map<string, CapabilityException>
  airportGroupMembers: Map<string, Set<string>>
  countryGroupMembers: Map<string, Set<string>>
  /** `regn|operatingDate` -> legs flown that day, for limit 1. */
  cyclesByTailDay: Map<string, number>
}

/** The two exception indexes the context needs, built together. */
export function exceptionIndexes(exceptions: CapabilityException[]) {
  return {
    countryExceptions: exceptionIndex(exceptions, 'country'),
    airportExceptions: exceptionIndex(exceptions, 'airport'),
  }
}

/**
 * Legs per tail in one operating day, for limit 1.
 *
 * A cycle is one leg — one take-off and one landing (confirmed with OMC,
 * 15/09/2026). The day is not midnight to midnight either: it is the **24
 * hours from the tail's first departure of that operating day**, so a rotation
 * that finishes after midnight is still counted against the day it started.
 * Counting to midnight would let a fifth leg at 00:30 pass a four-leg limit.
 *
 * Returned keyed by `regn|operatingDate` — the window a leg belongs to is the
 * window of the operating day its own row states.
 */
export function cycleIndex(legs: FlightLeg[]): Map<string, number> {
  const byTail = new Map<string, FlightLeg[]>()
  for (const leg of legs) {
    if (!leg.regn || leg.cancelledAt) continue
    const list = byTail.get(leg.regn)
    if (list) list.push(leg)
    else byTail.set(leg.regn, [leg])
  }

  const counts = new Map<string, number>()
  const DAY = 24 * 60 * 60_000
  for (const [regn, list] of byTail) {
    list.sort((a, b) => a.stdUtc - b.stdUtc)

    // First departure of each operating day opens that day's window.
    const opened = new Map<string, number>()
    for (const leg of list) {
      if (!opened.has(leg.operatingDate)) opened.set(leg.operatingDate, leg.stdUtc)
    }
    for (const [date, from] of opened) {
      let count = 0
      for (const leg of list) {
        if (leg.stdUtc < from) continue
        if (leg.stdUtc >= from + DAY) break
        count += 1
      }
      counts.set(`${regn}|${date}`, count)
    }
  }
  return counts
}

export function alertsFor(leg: FlightLeg, context: AlertContext): LegAlert[] {
  const alerts: LegAlert[] = []
  if (leg.cancelledAt) return alerts

  for (const station of [leg.dep, leg.arr]) {
    if (!context.airportsByIata.has(station)) {
      alerts.push(flag('unknownStation', station, 'Not in airports'))
    }
  }

  if (!leg.regn) return alerts
  const tail = context.fleetByRegn.get(leg.regn)
  if (!tail) {
    alerts.push(flag('unknownTail', leg.regn, 'Not in fleet'))
    return alerts
  }

  const column = fleetColumnKey(tail)
  const stations = leg.dep === leg.arr ? [leg.dep] : [leg.dep, leg.arr]

  for (const station of stations) {
    const airport = context.airportsByIata.get(station)

    // Tail tier first: it is the narrower statement, and when it refuses the
    // aircraft tier's verdict about the trio is beside the point.
    const barredHere = tailBlockedAtAirport(tail, station, context.airportGroupMembers)
    if (barredHere) {
      alerts.push(flag('tailAirport', station, barredHere.group ?? barredHere.detail))
    } else {
      const verdict = resolveAirport(
        context.airportCapability,
        context.airportExceptions.get(station),
        station,
        column,
        context.airportGroupMembers,
      )
      if (verdict.state === 'no') {
        alerts.push(flag('aircraftAirport', station, verdict.group ?? verdict.source))
      }
    }

    if (!airport?.countryCode) continue
    const barredThere = tailBlockedInCountry(tail, airport.countryCode, context.countryGroupMembers)
    if (barredThere) {
      alerts.push(flag('tailCountry', airport.countryCode, barredThere.group ?? barredThere.detail))
    } else {
      const verdict = resolveCountry(
        context.countryCapability,
        context.countryExceptions.get(airport.countryCode),
        airport.countryCode,
        column,
        context.countryGroupMembers,
      )
      if (verdict.state === 'no') {
        alerts.push(flag('aircraftCountry', airport.countryCode, verdict.group ?? verdict.source))
      }
    }
  }

  const overBlock = tailBlockedByBlockTime(tail, leg.blockMinutes)
  if (overBlock) {
    alerts.push(flag('tailBlock', `${leg.blockMinutes} min`, overBlock.detail))
  }

  const cycles = context.cyclesByTailDay.get(`${leg.regn}|${leg.operatingDate}`) ?? 0
  const overCycles = tailBlockedByCycles(tail, cycles)
  if (overCycles) {
    alerts.push(flag('tailCycles', `${cycles} legs`, overCycles.detail))
  }

  return alerts
}

function flag(kind: AlertKind, subject: string, detail: string): LegAlert {
  return { kind, severity: ALERT_SEVERITY[kind], subject, detail }
}

/** Wording lives in the UI layer, same rule as AuthError and ReferenceError. */
export const ALERT_LABEL: Record<AlertKind, string> = {
  tailCountry: 'Tail barred in country',
  tailAirport: 'Tail barred at station',
  tailBlock: 'Over max block',
  tailCycles: 'Over max cycles',
  aircraftCountry: 'Aircraft not allowed in country',
  aircraftAirport: 'Aircraft not allowed at station',
  unknownTail: 'Tail not in fleet',
  unknownStation: 'Station not in airports',
}
